import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaPaperPlane, FaEdit, FaBan, FaRedo, FaHandshake, FaUsers, FaCheck, FaTimes, FaQuestion, FaEnvelopeOpenText, FaFileSignature, FaMicrophone } from "react-icons/fa";
import DataTable from "../../../components/DataTable";
import StatusBadge from "../../../components/StatusBadge";
import KPICard from "../../../components/KPICard";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import { useToast } from "../../../components/Toast";
import { colors, spacing, typography, radius, cardBase, inputBase, btnPrimary, btnSecondary, btnDanger, btnGhost, pageTitle } from "../../../utils/designTokens";
import { asambleaAdmin, mensajeDeError, abrirModoSala, horaMontevideo, modoSalaDisponible, ventanaModoSala } from "../../../utils/asambleas/api";
import { useAhoraEnLimites } from "../../../utils/asambleas/reloj";
import AsambleaFormModal from "./AsambleaFormModal";
import CitacionesImprimibles from "./CitacionesImprimibles";
import AsambleaActaSeccion from "./AsambleaActaSeccion";
import AsambleaSalaSeccion, { textosAvisosZoom, textoMotivoZoom } from "./AsambleaSalaSeccion";
import ActasEditor from "../ActasEditor";
import { esRegistrada } from "./registrada";

// El acta se trabaja una vez convocada la asamblea (antes no hay nada que grabar).
const ESTADOS_CON_ACTA = new Set(["convocada", "en_curso", "finalizada"]);
// La sala de Zoom se crea después de convocar (asíncrona, la crea el cron).
const ESTADOS_CON_SALA = ESTADOS_CON_ACTA;

/** ¿La asamblea usa Zoom automático? (video es el resumen sin links que devuelve detalle) */
const usaZoom = (a) => !!a && !!a.video && a.video.proveedor === "zoom";

const CANAL = { whatsapp: "WhatsApp", email: "Email", en_mano: "En mano" };
const MOTIVO = {
  sin_contacto: "sin teléfono ni email",
  baja_whatsapp: "dio de baja WhatsApp",
  sin_whatsapp: "sin WhatsApp",
  sin_domicilio_electronico: "sin domicilio electrónico",
  domicilio_no_disponible: "su domicilio electrónico no está disponible",
  modo_prueba: "modo prueba",
};
const RSVP = { si: "asiste", no: "no_asiste" };

const fechaLarga = (a) =>
  new Date(Number(a.inicioMs)).toLocaleString("es-UY", {
    timeZone: "America/Montevideo", weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
  });

/**
 * Detalle de una asamblea: convocatoria, estado de cada citación y acciones.
 * Con Zoom automático (video.proveedor "zoom"), la sección "Sala": estado de
 * la sala, anfitrión y vista en vivo (AsambleaSalaSeccion). Guardar y
 * convocar devuelven avisos de Zoom que no bloquean (avisoLicencia: se
 * superpone con otra reunión; avisoCapacidad: más participantes que la sala):
 * quedan a la vista hasta que el staff los cierra. `avisosZoomIniciales` trae
 * los del alta (AsambleasAdmin).
 *
 * "Suspender" se ofrece solo hasta la hora de inicio (se apaga sola al llegar):
 * después la asamblea ya se hizo o se está haciendo, y el servidor lo rechaza
 * (sala_finalizada; si no hubo quórum, se deja constar en el acta). Los errores
 * de reprogramar y suspender con motivo conocido (tope diario de salas de Zoom
 * del edificio, sala ya cerrada) se explican con textoMotivoZoom.
 *
 * Una asamblea registrada después (no convocada por Habitia: esRegistrada) no
 * tiene citaciones, sala ni Modo Sala: el paso siguiente es subir su grabación
 * en la sección Acta.
 */
export default function AsambleaDetalle({ aid, edificioNombre, onVolver, onCambio, avisosZoomIniciales = null }) {
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [modal, setModal] = useState(null); // "editar" | "reprogramar" | "cancelar" | "avisos"
  const [errorForm, setErrorForm] = useState("");
  const [avisos, setAvisos] = useState([]);
  // Avisos de Zoom (textos) de la última vez que se guardó o convocó.
  const [avisosZoom, setAvisosZoom] = useState(() => textosAvisosZoom(avisosZoomIniciales));
  const [motivo, setMotivo] = useState("");
  const [pendienteConfirmar, setPendienteConfirmar] = useState(null); // datos de reprogramación a confirmar
  // Domicilio electrónico registrado en papel: { userid, nombre, canal, nota }
  const [domicilio, setDomicilio] = useState(null);
  // Editor de actas abierto dentro del detalle: { actaId } (null = acta nueva a mano).
  const [editor, setEditor] = useState(null);
  // Hora actual que se renueva sola a la hora de inicio (ahí deja de ofrecerse
  // "Suspender") y en los bordes del Modo Sala (de T-3 h a T+10 h).
  const ventanaSala = data && data.asamblea ? ventanaModoSala(data.asamblea) : null;
  const ahora = useAhoraEnLimites([
    data && data.asamblea ? Number(data.asamblea.inicioMs) : NaN,
    ventanaSala ? ventanaSala.desdeMs : NaN,
    ventanaSala ? ventanaSala.hastaMs : NaN,
  ]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await asambleaAdmin("detalle", { aid }));
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      setCargando(false);
    }
  }, [aid, showToast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const ejecutar = async (op, extra, ok) => {
    setTrabajando(true);
    try {
      const res = await asambleaAdmin(op, { aid, ...extra });
      if (res && res.requiereConfirmacion) {
        setAvisos(res.avisos || []);
        setModal("avisos");
        return res;
      }
      if (ok) showToast(ok, "success");
      await cargar();
      onCambio && onCambio();
      return res;
    } catch (e) {
      // Suspender una asamblea que ya se hizo (sala_finalizada) tiene su texto; el resto, el del servidor.
      showToast(textoMotivoZoom(e, op), "error");
      return null;
    } finally {
      setTrabajando(false);
    }
  };

  const convocar = async (confirmarAvisos = false) => {
    const res = await ejecutar("convocar", { confirmarAvisos }, null);
    if (res && res.ok) {
      setModal(null);
      setAvisosZoom(textosAvisosZoom(res));
      showToast(`Citación enviada a ${res.propietarios} propietario(s).`, "success");
    }
  };

  const guardarEdicion = async (datos, confirmarAvisos = false) => {
    setErrorForm("");
    const op = data.asamblea.estado === "borrador" ? "guardar" : "reprogramar";
    // proveedorVideo viaja en datos (lo normaliza el modelo) y también arriba del
    // request, por si la op lo lee de ahí (guardar/convocar aceptan proveedorVideo).
    const video = datos && datos.proveedorVideo ? { proveedorVideo: datos.proveedorVideo } : {};
    const extra = op === "guardar" ? { buildingId: data.asamblea.buildingid, datos, ...video } : { datos, confirmarAvisos, ...video };
    setTrabajando(true);
    try {
      const res = await asambleaAdmin(op, { aid, ...extra });
      if (res && res.requiereConfirmacion) {
        setPendienteConfirmar(datos);
        setAvisos(res.avisos || []);
        setModal("avisos");
        return;
      }
      setModal(null);
      setAvisosZoom(textosAvisosZoom(res));
      showToast(op === "reprogramar" && res && res.recitada ? "Se avisó del cambio a los propietarios." : "Asamblea guardada.", "success");
      await cargar();
      onCambio && onCambio();
    } catch (e) {
      // Reprogramar: tope diario de salas de Zoom del edificio y sala ya cerrada tienen su texto.
      setErrorForm(textoMotivoZoom(e, op));
    } finally {
      setTrabajando(false);
    }
  };

  // Datos iniciales del formulario: estables entre renders (si no, cada render del
  // detalle, p. ej. al guardar, reiniciaría lo que se está editando en el modal).
  const inicialForm = useMemo(
    () =>
      data && data.asamblea
        ? {
            ...data.asamblea,
            linkManual: data.linkManual,
            proveedorVideo: (data.asamblea.video && data.asamblea.video.proveedor) || null,
          }
        : null,
    [data]
  );

  if (cargando && !data) return <p style={{ color: colors.gray600 }}>Cargando…</p>;
  if (!data) return null;
  const a = data.asamblea;
  const r = a.resumen || {};
  const esBorrador = a.estado === "borrador";
  const convocada = a.estado === "convocada";
  // Desde la hora de inicio no se suspende: la asamblea ya se hizo o se está haciendo.
  const empezo = Number.isFinite(Number(a.inicioMs)) && ahora >= Number(a.inicioMs);
  const puedeSuspender = convocada && !empezo;
  const registrada = esRegistrada(a);
  // Modo Sala (presencial o mixta, el día de la asamblea): check-in, quórum en vivo y grabación desde el celular.
  // Una registrada después ya se hizo: no hay sala que abrir.
  const modoSala = registrada ? { mostrar: false, habilitado: false } : modoSalaDisponible(a, ahora);

  if (editor) {
    return <ActasEditor actaId={editor.actaId} asamblea={a} onVolver={() => setEditor(null)} />;
  }

  const columnas = [
    { key: "nombre", label: "Propietario", render: (v, fila) => v || `Userid ${fila.userid}` },
    { key: "unidades", label: "Unidades", render: (u) => (u || []).map((x) => x.etiqueta).join(", ") },
    {
      key: "canal",
      label: "Canal",
      render: (c, fila) =>
        `${CANAL[c] || c}${fila.porDomicilio ? " · domicilio electrónico" : ""}${fila.motivoCanal ? ` (${MOTIVO[fila.motivoCanal] || fila.motivoCanal})` : ""}`,
    },
    {
      key: "estadoEnvio",
      label: "Citación",
      render: (s, fila) => (
        <span title={fila.motivo ? MOTIVO[fila.motivo] || fila.motivo : undefined}>
          <StatusBadge status={`asm_${s}`} size="sm" />
        </span>
      ),
    },
    { key: "rsvp", label: "Asistencia", render: (v) => <StatusBadge status={RSVP[v] || "sin_respuesta"} size="sm" /> },
    {
      key: "acciones",
      label: "",
      align: "right",
      render: (_, fila) =>
        convocada ? (
          <span style={{ display: "inline-flex", gap: spacing[1] }}>
            {fila.canal !== "en_mano" && (
              <button style={btnGhost} className="habitia-btn-ghost" aria-label="Reenviar citación" title="Reenviar citación"
                onClick={() => ejecutar("reenviar", { userid: fila.userid }, "Citación reenviada.")} disabled={trabajando}>
                <FaRedo />
              </button>
            )}
            {fila.estadoEnvio !== "en_mano_entregada" && (
              <button style={btnGhost} className="habitia-btn-ghost" aria-label="Marcar entregada en mano" title="Marcar entregada en mano"
                onClick={() => ejecutar("marcarEnMano", { userid: fila.userid }, "Marcada como entregada en mano.")} disabled={trabajando}>
                <FaHandshake />
              </button>
            )}
            {!fila.porDomicilio && (
              <button style={btnGhost} className="habitia-btn-ghost" aria-label="Registrar domicilio electrónico (formulario en papel)"
                title="Registrar domicilio electrónico (formulario en papel)"
                onClick={() => setDomicilio({ userid: fila.userid, nombre: fila.nombre, canal: "whatsapp", nota: "" })} disabled={trabajando}>
                <FaFileSignature />
              </button>
            )}
          </span>
        ) : null,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
        <button style={btnGhost} className="habitia-btn-ghost" onClick={onVolver} aria-label="Volver a la lista"><FaArrowLeft /></button>
        <h1 style={{ ...pageTitle, margin: 0 }}>Asamblea {a.tipo} · {fechaLarga(a)}</h1>
        <StatusBadge status={a.estado} />
        {registrada && <StatusBadge status="en_proceso" label="Registrada después (no convocada por Habitia)" />}
        {a.version > 1 && <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Reprogramada ({a.version - 1} vez/veces)</span>}
      </div>

      <div style={{ ...cardBase, display: "flex", flexDirection: "column", gap: spacing[3] }}>
        <div style={{ fontSize: typography.sizes.md, color: colors.gray900 }}>
          {a.modalidad === "remota" ? "Por videollamada" : a.modalidad === "mixta" ? `Mixta: ${a.lugar} o videollamada` : `Presencial: ${a.lugar}`}
          {!registrada && ` · 2ª convocatoria a los ${a.minutosSegunda} minutos`}
        </div>
        <ol style={{ margin: 0, paddingLeft: 20, color: colors.gray900, fontSize: typography.sizes.md }}>
          {(a.ordenDelDia || []).map((p) => (
            <li key={p.id} style={{ marginBottom: spacing[1] }}>
              {p.titulo}
              {p.detalle && <div style={{ color: colors.gray600, fontSize: typography.sizes.base }}>{p.detalle}</div>}
            </li>
          ))}
        </ol>
        {a.modalidad !== "presencial" && usaZoom(a) ? (
          <div style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>
            Videollamada: Zoom automático.{" "}
            {esBorrador
              ? "La sala se crea sola después de convocar y cada propietario recibe su acceso personal 15 minutos antes."
              : "Cada propietario recibe su acceso personal 15 minutos antes."}
          </div>
        ) : (
          data.linkManual && (
            <div style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>
              <div>Link de la videollamada: {data.linkManual}</div>
              {a.modalidad !== "presencial" && <div>Los propietarios lo ven 15 minutos antes, en la app y en el enlace de su citación.</div>}
            </div>
          )
        )}
        <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap", marginTop: spacing[2] }}>
          {esBorrador && (
            <>
              <button style={btnPrimary} className="habitia-btn-primary" onClick={() => convocar(false)} disabled={trabajando}>
                <FaPaperPlane /> Convocar a los propietarios
              </button>
              <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => { setErrorForm(""); setModal("editar"); }} disabled={trabajando}>
                <FaEdit /> Editar
              </button>
            </>
          )}
          {convocada && (
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => { setErrorForm(""); setModal("reprogramar"); }} disabled={trabajando}>
              <FaEdit /> Reprogramar o corregir
            </button>
          )}
          {(esBorrador || puedeSuspender) && (
            <button style={btnDanger} className="habitia-btn-danger" onClick={() => { setMotivo(""); setModal("cancelar"); }} disabled={trabajando}>
              <FaBan /> {esBorrador ? "Descartar" : "Suspender"}
            </button>
          )}
          {modoSala.mostrar && (
            <button style={btnPrimary} className="habitia-btn-primary" onClick={() => abrirModoSala(aid)} disabled={!modoSala.habilitado}>
              <FaMicrophone /> Abrir Modo Sala
            </button>
          )}
        </div>
        {modoSala.mostrar && (
          <div style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>
            {a.modalidad === "mixta"
              ? "Modo Sala: check-in de la sala con el quórum en vivo y los puntos marcados (el audio sale de la videollamada), desde un celular o una tablet."
              : "Modo Sala: check-in con el quórum en vivo y la grabación de la asamblea, desde un celular o una tablet con micrófono externo."}
            {!modoSala.habilitado && modoSala.desdeMs ? ` Se habilita a las ${horaMontevideo(modoSala.desdeMs)}.` : ""}
          </div>
        )}
      </div>

      {avisosZoom.length > 0 && (
        <div style={S.avisoZoom} role="status" aria-label="Avisos de Zoom">
          <ul style={{ margin: 0, paddingLeft: spacing[5], display: "flex", flexDirection: "column", gap: spacing[2] }}>
            {avisosZoom.map((texto) => (
              <li key={texto}>{texto}</li>
            ))}
          </ul>
          <div>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setAvisosZoom([])}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {!registrada && usaZoom(a) && ESTADOS_CON_SALA.has(a.estado) && <AsambleaSalaSeccion asamblea={a} onCambio={cargar} />}

      {registrada && (
        <div style={S.siguientePaso} role="status">
          Siguiente paso: subí la grabación de la asamblea (audio o video) en la sección Acta. La IA arma el acta y la revisás antes de
          publicarla. Nadie fue citado por Habitia, así que la asistencia la confirmás en el acta.
        </div>
      )}

      {!esBorrador && !registrada && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: spacing[4] }}>
            <KPICard icon={FaUsers} label="Propietarios citados" value={r.total ?? 0} />
            <KPICard icon={FaEnvelopeOpenText} label="Citaciones entregadas" value={r.entregadas ?? 0} />
            <KPICard icon={FaCheck} iconBg={colors.successLight} iconColor={colors.success} label="Asisten" value={r.confirmados ?? 0} />
            <KPICard icon={FaTimes} iconBg={colors.gray100} iconColor={colors.gray600} label="No asisten" value={r.noAsisten ?? 0} />
            <KPICard icon={FaQuestion} iconBg={colors.warningLight} iconColor={colors.warning} label="Sin respuesta" value={r.sinRespuesta ?? 0} />
          </div>
          <div style={cardBase}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing[3], flexWrap: "wrap", marginBottom: spacing[3] }}>
              <h2 style={{ margin: 0, fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400 }}>Citaciones</h2>
              <CitacionesImprimibles asamblea={a} invitaciones={data.invitaciones} edificioNombre={edificioNombre} />
            </div>
            <DataTable columns={columnas} data={data.invitaciones} emptyIcon={FaUsers} emptyTitle="Sin citaciones" />
          </div>
        </>
      )}

      {ESTADOS_CON_ACTA.has(a.estado) && (
        <AsambleaActaSeccion asamblea={a} onAbrirEditor={(actaId) => setEditor({ actaId: actaId || null })} />
      )}

      <AsambleaFormModal
        open={modal === "editar" || modal === "reprogramar"}
        onClose={() => setModal(null)}
        onSubmit={(datos) => guardarEdicion(datos)}
        inicial={inicialForm}
        modo={modal === "reprogramar" ? "reprogramar" : "editar"}
        edificioNombre={edificioNombre}
        guardando={trabajando}
        error={errorForm}
        zoomDisponible={data.zoomDisponible === true}
      />

      <AdminModal
        open={modal === "avisos"}
        onClose={() => setModal(null)}
        title="Revisá los plazos"
        size="narrow"
        footer={
          <>
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => setModal(null)}>Volver</button>
            <button style={btnPrimary} className="habitia-btn-primary" disabled={trabajando}
              onClick={() => (pendienteConfirmar ? guardarEdicion(pendienteConfirmar, true).then(() => setPendienteConfirmar(null)) : convocar(true))}>
              Seguir igual
            </button>
          </>
        }
      >
        <ul style={{ margin: 0, paddingLeft: 20, color: colors.gray900, fontSize: typography.sizes.md }}>
          {avisos.map((x) => <li key={x.codigo} style={{ marginBottom: spacing[2] }}>{x.mensaje}</li>)}
        </ul>
      </AdminModal>

      <AdminModal
        open={!!domicilio}
        onClose={() => setDomicilio(null)}
        title="Registrar domicilio electrónico"
        size="narrow"
        footer={
          <>
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => setDomicilio(null)}>Volver</button>
            <button style={btnPrimary} className="habitia-btn-primary" disabled={trabajando}
              onClick={async () => {
                const res = await ejecutar("domicilio", {
                  accion: "registrar", buildingId: a.buildingid, userid: domicilio.userid, canal: domicilio.canal, nota: domicilio.nota,
                }, "Domicilio electrónico registrado. Vale para las próximas citaciones.");
                if (res) setDomicilio(null);
              }}>
              Registrar
            </button>
          </>
        }
      >
        {domicilio && (
          <>
            <p style={{ marginTop: 0, color: colors.gray900, fontSize: typography.sizes.md }}>
              {domicilio.nombre || `Userid ${domicilio.userid}`} firmó el formulario de domicilio electrónico. Queda para su contacto actual del padrón.
            </p>
            <FormField label="Canal" htmlFor="asm-dom-canal">
              <select id="asm-dom-canal" className="habitia-input" style={inputBase} value={domicilio.canal}
                onChange={(e) => setDomicilio({ ...domicilio, canal: e.target.value })}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </FormField>
            <FormField label="Nota (opcional)" htmlFor="asm-dom-nota" hint="Ej.: formulario firmado el 20/10, archivado en la carpeta del edificio.">
              <input id="asm-dom-nota" type="text" maxLength={300} className="habitia-input" style={inputBase} value={domicilio.nota}
                onChange={(e) => setDomicilio({ ...domicilio, nota: e.target.value })} />
            </FormField>
          </>
        )}
      </AdminModal>

      <AdminModal
        open={modal === "cancelar"}
        onClose={() => setModal(null)}
        title={esBorrador ? "Descartar asamblea" : "Suspender asamblea"}
        size="narrow"
        footer={
          <>
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => setModal(null)}>Volver</button>
            <button style={btnDanger} className="habitia-btn-danger" disabled={trabajando}
              onClick={async () => { await ejecutar("cancelar", { motivo }, esBorrador ? "Asamblea descartada." : "Asamblea suspendida."); setModal(null); }}>
              {esBorrador ? "Descartar" : "Suspender y avisar"}
            </button>
          </>
        }
      >
        {!esBorrador && (
          <p style={{ marginTop: 0, color: colors.gray900, fontSize: typography.sizes.md }}>
            Se avisa a los propietarios que ya recibieron la citación.
          </p>
        )}
        <FormField label="Motivo (opcional)" htmlFor="asm-motivo">
          <input id="asm-motivo" type="text" maxLength={300} className="habitia-input" style={inputBase} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </FormField>
      </AdminModal>
    </div>
  );
}

const S = {
  siguientePaso: {
    background: colors.infoLight,
    color: colors.infoDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  avisoZoom: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
};
